import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Calendar, MapPin, Clock, Video, Mic, Upload, Edit3 } from "lucide-react";
import { getAllProject } from "services/projectService";
import ProjectDetailsModal from "components/requirements/ProjectDetailsModal";
import { format } from "date-fns";
import Tabs from "components/generic/FilterTabs";
import BulkUploadProject from "./bulkUploadProject";

const platformNames = {
  1: "Twitch",
  2: "Youtube",
  3: "Facebook",
  4: "Twitter",
  5: "LinkedIn",
  6: "Custom RTMP",
};

const StatusBadge = ({ status }) => {
  const styles = {
    Active: "text-green-600 bg-green-100 border border-green-300",
    Completed: "text-blue-600 bg-blue-100 border border-blue-300",
    Cancelled: "text-red-600 bg-red-100 border border-red-300",
    Draft: "text-yellow-600 bg-yellow-100 border border-yellow-300",
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
};

const getProjectStatus = (project) => {
  if (project.is_completed) return "Completed";
  if (project.is_cancelled) return "Cancelled";
  if (project.is_draft) return "Draft";
  if (project.is_active) return "Active";
  return "Draft";
};

const RequirementHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [requirementsData, setRequirementsData] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkUpload, setIsBulkUpload] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const limit = 50;
  const [hasMore, setHasMore] = useState(true);

  // Filter state
  const [selectedTab, setSelectedTab] = useState(location.state?.activeTab || "All Bookings");

  // Get filter key for API
  const getFilterKey = () => {
    switch (selectedTab) {
      case "Completed Bookings":
        return "completed";
      case "Cancelled Bookings":
        return "cancelled";
      case "Draft":
        return "draft";
      default:
        return null;
    }
  };

  // Fetch data
  const fetchProjectData = async (reset = false, pageToFetch = 1) => {
    const filterKey = getFilterKey();
    let response;

    if (filterKey) {
      response = await getAllProject(filterKey);
    } else {
      response = await getAllProject(limit, pageToFetch);
    }

    if (response.success) {
      const newData = response.data?.projects || [];

      setRequirementsData((prev) =>
        reset ? newData : [...prev, ...newData]
      );

      if (!filterKey && newData.length < limit) {
        setHasMore(false);
      } else if (!filterKey) {
        setHasMore(true);
      }
    } else {
      console.error("Failed fetch:", response.error);
    }
  };

  useEffect(() => {
    if (location.state?.activeTab) {
      setSelectedTab(location.state.activeTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    setRequirementsData([]);
    setPage(1);
    setHasMore(true);
    fetchProjectData(true, 1);
  }, [selectedTab]);

  const handleViewMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProjectData(false, nextPage);
  };

  const openModal = (item) => {
    setSelectedRequirement(item);
    setIsModalOpen(true);
  };

  if (isBulkUpload) {
    return (
      <BulkUploadProject
        onBack={() => setIsBulkUpload(false)}
        onComplete={() => {
          setIsBulkUpload(false);
          fetchProjectData(true, 1);
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-sm md:max-w-[1600px] mx-auto relative">
      <h2 className="text-3xl font-semibold text-foreground mb-6">Shoot Requirement</h2>

      <div className="bg-card border border-border rounded-xl p-6 flex flex-col md:flex-row justify-between items-center mb-10 shadow-sm">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-semibold text-foreground">Book Your Shoot</h2>
          <p className="text-muted-foreground max-w-md">
            Create a new project and define your requirements.
          </p>
          <div className="flex gap-3 mt-2">
            <Button className="bg-button-coloured w-fit" onClick={() => navigate("/requirements")}>
              + Book A Shoot
            </Button>
            <Button variant="outline" className="flex gap-2" onClick={() => setIsBulkUpload(true)}>
              <Upload size={16} /> Bulk Upload
            </Button>
          </div>
        </div>
        <img src="/images/misc/bluebeige.png" alt="Banner" className="w-72 h-auto mt-6 md:mt-0 rounded-xl" />
      </div>

      <Tabs
        tabs={["All Bookings", "Completed Bookings", "Cancelled Bookings", "Draft"]}
        value={selectedTab}
        onChange={setSelectedTab}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {requirementsData.length > 0 ? (
          requirementsData.map((item) => {
            const status = getProjectStatus(item.project);

            return (
              <div key={item.project.stream_project_booking_id} className="bg-card border shadow-sm rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <StatusBadge status={status} />
                  <div className="flex -space-x-2 max-w-64 overflow-hidden md:max-w-full pl-2 md:pl-0">
                    {item.assignedCrew?.map((crew, index) => (
                      <img key={index} src="https://i.pravatar.cc/30"
                        className="w-8 h-8 rounded-full border-2 border-white" />
                    ))}
                  </div>
                </div>

                <h3 className="text-xl font-semibold mb-2">{item.project.project_name}</h3>

                <div className="flex flex-wrap gap-6 text-sm mb-4">
                  <div className="flex items-center gap-2"><Calendar size={16} /> {item.project.event_date}</div>
                  <div className="flex items-center gap-2"><MapPin size={16} /> {item.project.event_location}</div>
                  <div className="flex items-center gap-2"><Clock size={16} /> {item.project.duration_hours} hours</div>
                </div>

                <hr className="my-4" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <p className="text-xs font-medium mb-2">Streaming Platforms</p>
                    <div className="flex flex-wrap gap-2 max-h-[90px] overflow-y-auto pr-1">
                      {(Array.isArray(item.project.streaming_platforms)
                        ? item.project.streaming_platforms
                        : JSON.parse(item.project.streaming_platforms || "[]")
                      ).map((platformId, index) => (
                        <span key={index} className="border px-3 py-1 rounded-md text-sm bg-popover flex items-center gap-2 whitespace-nowrap">
                          <Video size={14} /> {platformNames[platformId] || "Unknown"}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium mb-2">Equipment Assigned</p>
                    <div className="flex flex-wrap gap-2 max-h-[90px] overflow-y-auto pr-1">
                      {item.assignedEquipment?.map((equipment, index) => (
                        <span key={index} className="border px-3 py-1 rounded-md text-sm bg-popover flex items-center gap-2 whitespace-nowrap">
                          <Mic size={14} /> {equipment.equipment.equipment_name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <div className="flex justify-between items-center">
                  {status === "Draft" ? (
                    <Button
                      className="bg-button-coloured px-5 py-2 rounded-md ml-auto flex items-center gap-2"
                      onClick={() => navigate(`/requirements/${item.project.stream_project_booking_id}`)}
                    >
                      <Edit3 size={16} /> Continue Editing
                    </Button>
                  ) : (
                    <Button
                      className="bg-button-coloured px-5 py-2 rounded-md ml-auto"
                      onClick={() => openModal(item)}
                    >
                      View Details
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-center text-muted-foreground mt-4">
            No bookings available
          </p>
        )}
      </div>

      {hasMore && selectedTab === "All Bookings" && (
        <div className="flex justify-center mt-6">
          <Button
            className="bg-button-coloured px-8 py-3"
            onClick={handleViewMore}
          >
            View More
          </Button>
        </div>
      )}

      <ProjectDetailsModal open={isModalOpen} onOpenChange={setIsModalOpen} project={selectedRequirement} />
    </div>
  );
};

export default RequirementHomePage;
